/**
 * События потока поиска по фото (`POST /api/platform/v1/search/photo/stream`, NDJSON; `Platform.Contracts.Search.PhotoSearchEvent`).
 * Одна строка — одно событие: `plan` (этапы и «обычно»), `stage` (этап идёт/готов/сбой/пропущен, с числами), `preliminary` (список кандидатов до итога: ранний по тексту и штрихкоду, пока считается фото, или объединённый до долгой сверки), `result` или `error`.
 */
import { list, optionalText, record, text } from '../../../shared/decode'
import type { StageEvent, TextQuery } from '../model/searchRun'
import type { PhotoRecognition, SearchResponse } from '../model/types'
import { decodeRecognition, decodeSearchResponse } from './contracts'

export interface StreamProblem {
  status: number
  code: string
  message: string
  correlationId: string | null
  components: readonly { name: string; status: string; detail: string | null }[]
}

export type StreamEvent =
  | StageEvent
  | { type: 'preliminary'; result: SearchResponse; note: string | null; correlationId: string | null; source: string | null; refining: boolean }
  | { type: 'result'; result: SearchResponse; correlationId: string | null }
  | { type: 'error'; problem: StreamProblem }

const number = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

function decodeCounts(raw: unknown): Record<string, number> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {}
  const counts: Record<string, number> = {}
  for (const [name, value] of Object.entries(raw)) if (typeof value === 'number') counts[name] = value
  return counts
}

function decodeQuery(raw: unknown, name: string): TextQuery {
  const item = record(raw, name)
  return {
    query: text(item.query, `${name}.query`),
    weight: number(item.weight) ?? 0,
    scope: text(item.scope, `${name}.scope`),
    found: number(item.found) ?? 0,
    elapsedMs: number(item.elapsedMs) ?? 0,
  }
}

export function decodeStreamEvent(raw: unknown): StreamEvent {
  const item = record(raw, 'event')
  const type = text(item.type, 'event.type')
  switch (type) {
    case 'plan':
      return {
        type,
        plan: list(item.plan ?? [], 'event.plan', (entry, name) => {
          const stage = record(entry, name)
          return { stage: text(stage.stage, `${name}.stage`), typicalMs: number(stage.typicalMs) }
        }),
        correlationId: optionalText(item.correlationId, 'event.correlationId'),
      }
    case 'stage': {
      const recognized: PhotoRecognition | null = decodeRecognition(item.recognized)
      return {
        type,
        stage: text(item.stage, 'event.stage'),
        state: text(item.state, 'event.state'),
        elapsedMs: number(item.elapsedMs),
        counts: decodeCounts(item.counts),
        recognized,
        queries: list(item.queries ?? [], 'event.queries', decodeQuery),
        note: optionalText(item.note, 'event.note'),
      }
    }
    case 'preliminary':
      // `source`/`refining` — аддитивные поля: прежний сервер их не шлёт, и тогда выдача считается «ждёт сверки», а не «уточняется по фото».
      return {
        type,
        result: decodeSearchResponse(item.result),
        note: optionalText(item.note, 'event.note'),
        correlationId: optionalText(item.correlationId, 'event.correlationId'),
        source: optionalText(item.source, 'event.source'),
        refining: item.refining === true,
      }
    case 'result':
      return { type, result: decodeSearchResponse(item.result), correlationId: optionalText(item.correlationId, 'event.correlationId') }
    case 'error': {
      const problem = record(item.problem, 'event.problem')
      return {
        type,
        problem: {
          status: number(item.status) ?? 500,
          code: text(problem.code, 'problem.code'),
          message: text(problem.message, 'problem.message'),
          correlationId: optionalText(item.correlationId, 'event.correlationId'),
          components: list(problem.components ?? [], 'problem.components', (entry, name) => {
            const component = record(entry, name)
            return {
              name: text(component.name, `${name}.name`),
              status: text(component.status, `${name}.status`),
              detail: optionalText(component.detail, `${name}.detail`),
            }
          }),
        },
      }
    }
    default:
      throw new Error(`неизвестное событие потока: ${type}`)
  }
}
